export default {
  en: {
    title: 'SSO Integration',
    subtitle: 'Integrate your company SSO IdP',
    advancedSettings: 'Advanced Settings',
    advancedSettingsAlert: 'Users will be denied access if these advanced settings do not match the IdP configuration.',
    success: 'Changes saved successfully',
    error: 'SSO integration error',
    form: {
      type: {
        hint: 'Select the SSO you want to integrate',
        label: 'Type of SSO'
      },
      enabled: {
        label: 'Status of SSO',
        hint: 'Enable or disable SSO integration',
        alert: 'Disabling the SSO integration will not expire the active sessions. New ones after disabling should use username/password combination.',
      },
      cert: {
        label: 'Public Certificate',
        hint: 'X.509 certificate from your Identity Provider',
        error: 'The X.509 certificate is required',
        errorType: 'The certificate is not a valid X.509 in PEM format',
        alert: 'The certificate will be encrypted upon upload and won\'t be visible here afterward.',
      },
      entryPoint: {
        label: 'Entry Point URL',
        hint: 'SAML URL where authentication requests should be sent',
        error: 'Entry Point URL is required',
        errorType: 'Valid URL is required',
      },
      issuer: {
        label: 'Issuer Entity ID',
        hint: 'Unique identifier for your service provider',
        error: 'Issuer entity ID is required',
      },
      wantAuthnResponseSigned: {
        label: 'Signed Authentication Response',
        hint: 'Require the authentication response to be signed by the IdP',
      },
      wantAssertionsSigned: {
        label: 'Signed Assertions',
        hint: 'Require SAML assertions to be signed by the IdP',
      },
      signatureAlgorithm: {
        label: 'Signature Algorithm',
        hint: 'Algorithm used for signing SAML messages (consult your IdP documentation)',
        options: {
          sha256: 'SHA256 (Recommended)',
          sha512: 'SHA512',
          sha1: 'SHA1 (Legacy - Not Recommended)'
        }
      },
      digestAlgorithm: {
        label: 'Digest Algorithm',
        hint: 'Algorithm used for message digests (consult your IdP documentation)',
        options: {
          sha256: 'SHA256 (Recommended)',
          sha512: 'SHA512',
          sha1: 'SHA1 (Legacy - Not Recommended)'
        }
      },
      forceAuthn: {
        label: 'Force Authentication',
        hint: 'Force users to re-authenticate even if they have an active session',
        options: {
          yes: 'Force re-authenticate users every time',
          no: 'Reuse active sessions'
        },
      },
      authnRequestBinding: {
        label: 'Authentication Request Binding',
        hint: 'SAML protocol binding to use for authentication requests',
      },
      samlSigning: {
        label: 'SAML signing certificate',
        hint: 'Manage the signing option used by the IdP',
        options: {
          assertions: 'Signed Assertions',
          response: 'Signed Authentication Response',
          assertionsAndResponse: 'Signed Authentication Response and Assertions'
        }
      },
      identifierFormat: {
        label: 'NameID Format',
        hint: 'Format of the NameID used in SAML assertions',
      }
    },
  },
  es: {
    title: 'Integración SSO',
    subtitle: 'Integre el IdP SSO de su empresa',
    advancedSettings: 'Configuración Avanzada',
    advancedSettingsAlert: 'Se denegará el acceso a los usuarios si esta configuración avanzada no coincide con la configuración del IdP.',
    success: 'Cambios guardados exitosamente',
    error: 'Error en la integración SSO',
    form: {
      type: {
        hint: 'Seleccione el SSO que desea integrar',
        label: 'Tipo de SSO'
      },
      enabled: {
        label: 'Estado del SSO',
        hint: 'Activar o desactivar la integración SSO',
        alert: 'Desactivar la integración SSO no expirará las sesiones activas. Las nuevas sesiones después de la desactivación deberán usar la combinación de usuario/contraseña.',
      },
      cert: {
        label: 'Certificado Público',
        hint: 'Certificado X.509 de su Proveedor de Identidad',
        error: 'El certificado X.509 es requerido',
        errorType: 'El certificado no es un X.509 válido en formato PEM',
        alert: 'El certificado será encriptado al subirlo y no será visible aquí después.',
      },
      entryPoint: {
        label: 'URL de Punto de Entrada',
        hint: 'URL SAML donde se deben enviar las solicitudes de autenticación',
        error: 'La URL del punto de entrada es requerida',
        errorType: 'Se requiere una URL válida',
      },
      issuer: {
        label: 'ID de Entidad Emisora',
        hint: 'Identificador único para su proveedor de servicios',
        error: 'El ID de entidad emisora es requerido',
      },
      wantAuthnResponseSigned: {
        label: 'Respuesta de Autenticación Firmada',
        hint: 'Requerir que la respuesta de autenticación esté firmada por el IdP',
      },
      wantAssertionsSigned: {
        label: 'Aserciones Firmadas',
        hint: 'Requerir que las aserciones SAML estén firmadas por el IdP',
      },
      signatureAlgorithm: {
        label: 'Algoritmo de Firma',
        hint: 'Algoritmo usado para firmar mensajes SAML (consulte la documentación de su IdP)',
        options: {
          sha256: 'SHA256 (Recomendado)',
          sha512: 'SHA512',
          sha1: 'SHA1 (Heredado - No Recomendado)'
        }
      },
      digestAlgorithm: {
        label: 'Algoritmo de Resumen',
        hint: 'Algoritmo usado para resúmenes de mensajes (consulte la documentación de su IdP)',
        options: {
          sha256: 'SHA256 (Recomendado)',
          sha512: 'SHA512',
          sha1: 'SHA1 (Heredado - No Recomendado)'
        }
      },
      forceAuthn: {
        label: 'Forzar Autenticación',
        hint: 'Forzar a los usuarios a reautenticarse incluso si tienen una sesión activa',
        options: {
          yes: 'Forzar reautenticación de usuarios cada vez',
          no: 'Reutilizar sesiones activas'
        },
      },
      authnRequestBinding: {
        label: 'Vinculación de Solicitud de Autenticación',
        hint: 'Vinculación del protocolo SAML a usar para solicitudes de autenticación',
      },
      samlSigning: {
        label: 'Certificado de firma SAML',
        hint: 'Gestionar la opción de firma utilizada por el IdP',
        options: {
          assertions: 'Aserciones Firmadas',
          response: 'Respuesta de Autenticación Firmada',
          assertionsAndResponse: 'Respuesta de Autenticación y Aserciones Firmadas'
        }
      },
      identifierFormat: {
        label: 'Formato de NameID',
        hint: 'Formato del NameID utilizado en las aserciones SAML',
      }
    },
  },
  fr: {
    title: 'Intégration SSO',
    subtitle: 'Intégrez l\'IdP SSO de votre entreprise',
    advancedSettings: 'Paramètres Avancés',
    advancedSettingsAlert: 'L\'accès sera refusé aux utilisateurs si ces paramètres avancés ne correspondent pas à la configuration de l\'IdP.',
    success: 'Modifications enregistrées avec succès',
    error: 'Erreur d\'intégration SSO',
    form: {
      type: {
        hint: 'Sélectionnez le SSO que vous souhaitez intégrer',
        label: 'Type de SSO'
      },
      enabled: {
        label: 'Statut du SSO',
        hint: 'Activer ou désactiver l\'intégration SSO',
        alert: 'La désactivation de l\'intégration SSO n\'expirera pas les sessions actives. Les nouvelles sessions après désactivation devront utiliser la combinaison nom d\'utilisateur/mot de passe.',
      },
      cert: {
        label: 'Certificat Public',
        hint: 'Certificat X.509 de votre Fournisseur d\'Identité',
        error: 'Le certificat X.509 est requis',
        errorType: 'Le certificat n\'est pas un X.509 valide au format PEM',
        alert: 'Le certificat sera crypté lors du téléchargement et ne sera plus visible ici par la suite.',
      },
      entryPoint: {
        label: 'URL du Point d\'Entrée',
        hint: 'URL SAML où les demandes d\'authentification doivent être envoyées',
        error: 'L\'URL du point d\'entrée est requise',
        errorType: 'Une URL valide est requise',
      },
      issuer: {
        label: 'ID d\'Entité Émettrice',
        hint: 'Identifiant unique pour votre fournisseur de services',
        error: 'L\'ID d\'entité émettrice est requis',
      },
      wantAuthnResponseSigned: {
        label: 'Réponse d\'Authentification Signée',
        hint: 'Exiger que la réponse d\'authentification soit signée par l\'IdP',
      },
      wantAssertionsSigned: {
        label: 'Assertions Signées',
        hint: 'Exiger que les assertions SAML soient signées par l\'IdP',
      },
      signatureAlgorithm: {
        label: 'Algorithme de Signature',
        hint: 'Algorithme utilisé pour signer les messages SAML (consultez la documentation de votre IdP)',
        options: {
          sha256: 'SHA256 (Recommandé)',
          sha512: 'SHA512',
          sha1: 'SHA1 (Obsolète - Non Recommandé)'
        }
      },
      digestAlgorithm: {
        label: 'Algorithme de Digest',
        hint: 'Algorithme utilisé pour les condensés de messages (consultez la documentation de votre IdP)',
        options: {
          sha256: 'SHA256 (Recommandé)',
          sha512: 'SHA512',
          sha1: 'SHA1 (Obsolète - Non Recommandé)'
        }
      },
      forceAuthn: {
        label: 'Forcer l\'Authentification',
        hint: 'Forcer les utilisateurs à se réauthentifier même s\'ils ont une session active',
        options: {
          yes: 'Forcer la réauthentification des utilisateurs à chaque fois',
          no: 'Réutiliser les sessions actives'
        },
      },
      authnRequestBinding: {
        label: 'Liaison de Demande d\'Authentification',
        hint: 'Liaison du protocole SAML à utiliser pour les demandes d\'authentification',
      },
      samlSigning: {
        label: 'Certificat de signature SAML',
        hint: 'Gérer l\'option de signature utilisée par l\'IdP',
        options: {
          assertions: 'Assertions Signées',
          response: 'Réponse d\'Authentification Signée',
          assertionsAndResponse: 'Réponse d\'Authentification et Assertions Signées'
        }
      },
      identifierFormat: {
        label: 'Format de NameID',
        hint: 'Format du NameID utilisé dans les assertions SAML',
      }
    },
  }
};
